import { useEffect, useState } from 'react'
import { loadActiveHighlight, type ActiveHighlight } from '../lib/highlights'

const POSITION_ICON: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
}

const NEW_WINDOW_MS = 24 * 60 * 60 * 1000

function isNewHighlight(startsAt: string) {
  const started = new Date(startsAt).getTime()
  const elapsed = Date.now() - started
  return Number.isFinite(started) && elapsed >= 0 && elapsed < NEW_WINDOW_MS
}

export function HighlightsBanner() {
  const [highlight, setHighlight] = useState<ActiveHighlight | null>(null)
  const [animateEntry, setAnimateEntry] = useState(false)

  useEffect(() => {
    let mounted = true
    loadActiveHighlight()
      .then((data) => mounted && setHighlight(data))
      .catch(() => mounted && setHighlight(null))
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    if (!highlight) return
    const storageKey = `suite-highlight-seen:${highlight.id}`
    try {
      if (!window.sessionStorage.getItem(storageKey)) {
        setAnimateEntry(true)
        window.sessionStorage.setItem(storageKey, '1')
      }
    } catch {
      setAnimateEntry(true)
    }
  }, [highlight?.id])

  if (!highlight || highlight.items.length === 0) return null

  const isRanking = highlight.mode === 'ranking'
  const showNew = isNewHighlight(highlight.starts_at)

  return (
    <section
      className={`highlights-banner highlights-banner--${Math.min(highlight.items.length, 3)} ${isRanking ? 'is-ranking' : 'is-recognition'} ${animateEntry ? 'is-first-view' : ''}`}
      aria-label="Destaques da equipe"
    >
      {animateEntry && <div className="highlights-banner__shine" aria-hidden="true" />}
      <header className="highlights-banner__head">
        <div className="highlights-banner__trophy" aria-hidden="true">🏆</div>
        <div className="highlights-banner__heading">
          <p className="eyebrow">{isRanking ? 'RANKING EM DESTAQUE' : 'RECONHECIMENTO EM DESTAQUE'}</p>
          <h2>{highlight.title}</h2>
          {highlight.subtitle && <p>{highlight.subtitle}</p>}
        </div>
        {showNew && <span className="highlights-banner__new" aria-label="Destaque novo">NOVO</span>}
      </header>

      <div className="highlights-banner__grid">
        {highlight.items
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((item) => (
            <article className={`highlight-person ${isRanking ? `highlight-person--${item.position}` : 'highlight-person--recognition'}`} key={item.user_id}>
              <div className="highlight-person__medal" aria-hidden="true">{isRanking ? (POSITION_ICON[item.position] ?? '🏅') : '🏆'}</div>
              <div className="highlight-person__copy">
                <span className="highlight-person__position">{isRanking ? `${item.position}º lugar` : 'Destaque'}</span>
                <strong>{item.nome}</strong>
                <small>{item.unit_label}</small>
                <p>{item.reason}</p>
              </div>
            </article>
          ))}
      </div>
    </section>
  )
}
